import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Nav } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { additionalMenuItems, defaultMenuItems } from '../../config/menu';
import { useWhatsAppData } from '../../hooks/useWhatsAppData';
import NavItem from './NavItem';

/** Environment configuration for white label */
const WHITE_LABEL_PERKESO = process.env.REACT_APP_WHITE_LABEL_PERKESO === 'true';

/**
 * Custom hook to generate menu items based on WhatsApp configuration
 * @param {Object} waCombinedData - WhatsApp configuration data
 * @returns {Array} Combined array of menu items
 */
const useMenuItems = (waCombinedData) => {
  return useMemo(() => {
    const items = [...defaultMenuItems];

    items.push(...additionalMenuItems);

    // Add WhatsApp menu item if not in WHITE_LABEL_PERKESO mode
    if (!WHITE_LABEL_PERKESO) {
      const firstItem = waCombinedData?.data?.combinedData?.[0];

      // More robust validation to check if WhatsApp configuration is valid
      const hasWhatsAppConfig = Boolean(
        firstItem?.waba_id &&
          firstItem.phoneNumbers?.data?.length > 0 &&
          firstItem.phoneNumbers?.data[0]?.id
      );

      // Additional check to ensure firstItem is not empty/undefined
      const isPhoneNumberAvailable =
        hasWhatsAppConfig &&
        Array.isArray(firstItem.phoneNumbers?.data) &&
        firstItem.phoneNumbers.data.length > 0;

      const insertIndex = defaultMenuItems.length;
      // Default to getting started path
      let whatsAppPath = '/dashboard/whatsapp';

      // If user has WhatsApp config, use the detailed path
      if (isPhoneNumberAvailable) {
        whatsAppPath = `/dashboard/whatsapp/phone-number-details/${firstItem.waba_id}/${firstItem.phoneNumbers.data[0].id}`;
      } else if (waCombinedData?.data?.wabaToken) {
        // User doesn't have any token yet. Send to getting started page
        whatsAppPath = '/dashboard/whatsapp/getting-started';
      }

      items.splice(insertIndex, 0, {
        path: whatsAppPath,
        icon: 'WhatsAppLogo',
        label: 'WhatsApp',
        isActiveCheck: (pathname) => pathname.startsWith('/dashboard/whatsapp'),
      });
    }

    return items;
  }, [waCombinedData]);
};

/**
 * Sidebar navigation items component
 * @param {Object} props - Component props
 * @param {boolean} props.sidebarShow - Controls sidebar visibility
 */
const SidebarItems = ({ sidebarShow }) => {
  const { pathname } = useLocation();
  const { access_token } = useSelector((state) => state.auth.user);
  const { data: waCombinedData, error } = useWhatsAppData('get-combined-data', access_token?.token);
  const items = useMenuItems(waCombinedData);

  if (error) {
    console.error('Error in WhatsApp data fetch:', error);
  }

  const isItemActive = (item) => {
    return !item.external && (item.isActiveCheck?.(pathname) ?? pathname === item.path);
  };

  return (
    <Nav as="ul" className="responsive-scroll">
      {items.map((item) => (
        <NavItem
          key={item.path}
          item={item}
          isActive={isItemActive(item)}
          sidebarShow={sidebarShow}
        />
      ))}
    </Nav>
  );
};

SidebarItems.propTypes = {
  sidebarShow: PropTypes.bool.isRequired,
};

export default SidebarItems;
